import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { EMPLOYEE_STRINGS } from 'accounts/constants/strings/employee.strings';
import { useListings } from 'accounts/hooks/homeowners/use-listings';
import { isNil } from 'es-toolkit/compat';
import { EmployeeAssignmentTypes, ReportType } from 'models/enums';
import { selectUserReports } from 'store/redux/user/selectors';
import { useSelectedProperty } from './use-selected-property';

export function useTeamEmployees({
  properties,
  units,
}: {
  properties: any;
  units: any;
}) {
  const { selectedProperty } = useSelectedProperty();
  const { listings } = useListings();
  const reports = useSelector(selectUserReports);

  const employees = useMemo(() => {
    if (!(properties && reports && listings && units && selectedProperty)) {
      return [];
    }

    const selectedUnit = selectedProperty.getUnitsWithAgreements()?.[0];

    if (!selectedUnit) {
      return [];
    }

    const homeListing = listings.find(
      (listing: any) =>
        listing.homeListingDetailsModel.basicInfo.unitId ===
        selectedUnit?.basicInfo.unitId,
    );

    const improvementsReportsByHomeId = reports.filter(
      ({ report }: any) =>
        report.homeId === selectedUnit?.basicInfo.unitId &&
        report.type === ReportType.Improvements,
    );

    const improvementsEmployees = improvementsReportsByHomeId?.flatMap(
      (report: any) => report.employeeAssignments,
    );

    let newEmployees = [
      ...selectedUnit.employeeAssignments,
      ...improvementsEmployees,
      ...(homeListing?.homeListingDetailsModel.employeeAssignments || []),
    ];

    const improvementsSpecialist = newEmployees.find(
      (employee) =>
        employee?.employeeAssignment?.assignmentType ===
        EmployeeAssignmentTypes.ImprovementsSpecialist,
    );

    const concierge = newEmployees.find(
      (employee) =>
        employee?.employeeAssignment?.assignmentType ===
        EmployeeAssignmentTypes.Concierge,
    );

    const homeownerGrowth = newEmployees.find(
      (employee) =>
        employee?.employeeAssignment?.assignmentType ===
        EmployeeAssignmentTypes.HomeownerGrowth,
    );

    const mainPointOfContact = concierge ?? homeownerGrowth;

    const mainPointOfContactWithOrder = mainPointOfContact
      ? {
          ...mainPointOfContact,
          order: [
            EmployeeAssignmentTypes.HomeownerGrowth,
            EmployeeAssignmentTypes.Concierge,
          ].includes(mainPointOfContact?.employeeAssignment?.assignmentType),
        }
      : null;

    const residentGrowth = newEmployees.find(
      (employee) =>
        employee?.employeeAssignment?.assignmentType ===
        EmployeeAssignmentTypes.ResidentGrowth,
    );

    newEmployees = [
      ...(residentGrowth
        ? [mainPointOfContactWithOrder, residentGrowth]
        : [mainPointOfContactWithOrder]),
      improvementsSpecialist,
    ];

    newEmployees = newEmployees.filter(
      (employee) =>
        // Just in case find somehow doesn't find the item we have undefined elements
        !isNil(employee),
    );

    return newEmployees
      .map(({ employee, employeeAssignment, user, ...rest }) => ({
        ...employeeAssignment,
        ...employee,
        ...user,
        profileImage: user?.profileImageUrl,
        bannerImage: employee?.bannerImageUrl ?? employee?.bannerUrl,
        jobTitle: EMPLOYEE_STRINGS[employeeAssignment?.assignmentType],
        ...rest,
      }))
      .filter(
        (formattedEmployee) =>
          formattedEmployee.bannerImage &&
          formattedEmployee.profileImage &&
          formattedEmployee.profileImageUrl,
      );
  }, [listings, properties, reports, selectedProperty, units]);

  const loading = !(
    properties &&
    reports &&
    listings &&
    units &&
    selectedProperty
  );

  return { employees, loading };
}
